import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Popconfirm, Space, Spin, Tag, Typography, message } from "antd";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  apiErrorMessage,
  formatDateTime,
  isManuallyDisabled,
  subscriptionStatus,
  toSubscriptionRequest,
} from "../labels";
import { FALLBACK_EVENT_TYPES, WebhookFormValues, WebhookTestResult } from "../types";
import { useWebhookApi, webhookKeys } from "../useWebhookApi";
import { WebhookDeliveries } from "./WebhookDeliveries";
import { WebhookSubscriptionFields } from "./WebhookSubscriptionFields";

const keyBoxStyle: React.CSSProperties = {
  background: "#f7f8fa",
  border: "1px solid #eef0f3",
  borderRadius: 8,
  padding: "12px 16px",
};

export const WebhookDetailView: React.FC = () => {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const created = searchParams.get("created") === "1";
  const api = useWebhookApi();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<WebhookFormValues>();
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);
  const hydratedId = useRef<number | null>(null);

  const subscriptionQuery = useQuery({
    queryKey: webhookKeys.subscription(id),
    queryFn: () => api.getSubscription(id),
    enabled: Number.isFinite(id) && id > 0,
    retry: false,
  });

  const eventTypesQuery = useQuery({
    queryKey: webhookKeys.eventTypes,
    queryFn: async () => {
      const result = await api.listEventTypes();
      return Array.isArray(result) && result.length > 0 ? result : FALLBACK_EVENT_TYPES;
    },
  });

  const eventTypes = eventTypesQuery.data ?? FALLBACK_EVENT_TYPES;
  const subscription = subscriptionQuery.data;

  useEffect(() => {
    setTestResult(null);
  }, [id]);

  useEffect(() => {
    if (!subscription || hydratedId.current === subscription.id) {
      return;
    }
    hydratedId.current = subscription.id;
    form.setFieldsValue({
      name: subscription.name,
      url: subscription.url,
      apiKeyHeaderName: subscription.apiKeyHeaderName,
      eventTypes: subscription.eventTypes,
    });
  }, [subscription, form]);

  const refreshSubscription = (nextId = id) => {
    queryClient.invalidateQueries({ queryKey: webhookKeys.subscription(nextId) });
    queryClient.invalidateQueries({ queryKey: webhookKeys.list });
  };

  const saveMutation = useMutation({
    mutationFn: (values: WebhookFormValues) => api.updateSubscription(id, toSubscriptionRequest(values)),
    onSuccess: () => {
      refreshSubscription();
      message.success("Webhooken sparades");
    },
    onError: (error) => message.error(apiErrorMessage(error, "Kunde inte spara webhooken")),
  });

  const regenerateMutation = useMutation({
    mutationFn: () => api.regenerateKey(id),
    onSuccess: () => {
      refreshSubscription();
      message.success("Ny API-nyckel skapad. Uppdatera mottagaren.");
    },
    onError: (error) => message.error(apiErrorMessage(error, "Kunde inte skapa en ny nyckel")),
  });

  const enableMutation = useMutation({
    mutationFn: () => api.enableSubscription(id),
    onSuccess: () => {
      refreshSubscription();
      message.success("Webhooken är aktiverad");
    },
    onError: (error) => message.error(apiErrorMessage(error, "Kunde inte aktivera webhooken")),
  });

  const disableMutation = useMutation({
    mutationFn: () => api.disableSubscription(id),
    onSuccess: () => {
      refreshSubscription();
      message.success("Webhooken är avstängd. Väntande leveranser ligger kvar.");
    },
    onError: (error) => message.error(apiErrorMessage(error, "Kunde inte stänga av webhooken")),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteSubscription(id),
  });

  const testMutation = useMutation({
    mutationFn: () => api.sendTest(id),
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(id) });
      refreshSubscription();
    },
    onError: (error) => message.error(apiErrorMessage(error, "Testet kunde inte skickas")),
  });

  if (!Number.isFinite(id) || id <= 0) {
    return <Alert type="error" showIcon message="Ogiltig webhook" />;
  }

  if (subscriptionQuery.isLoading) {
    return <Spin />;
  }

  if (subscriptionQuery.isError || !subscription) {
    return (
      <Alert
        type="error"
        showIcon
        message={apiErrorMessage(subscriptionQuery.error, "Webhooken hittades inte")}
        action={<Link to="/webhooks">Tillbaka</Link>}
      />
    );
  }

  const status = subscriptionStatus(subscription);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      queryClient.invalidateQueries({ queryKey: webhookKeys.list });
      message.success("Webhooken raderades");
      navigate("/webhooks");
    } catch (error) {
      message.error(apiErrorMessage(error, "Kunde inte radera webhooken"));
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card bordered={false}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Link to="/webhooks">Tillbaka till webhooks</Link>
          <Space align="start" wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical" size={4}>
              <Space wrap>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {subscription.name}
                </Typography.Title>
                <Tag color={status.color}>{status.label}</Tag>
              </Space>
              <Typography.Text type="secondary">{subscription.url}</Typography.Text>
            </Space>
            <Space wrap>
              {subscription.isActive ? (
                <Popconfirm
                  title="Stäng av webhooken?"
                  description="Inga nya händelser skickas. Leveranser som redan väntar ligger kvar tills ni aktiverar igen."
                  okText="Stäng av"
                  cancelText="Avbryt"
                  onConfirm={() => disableMutation.mutate()}
                >
                  <Button loading={disableMutation.isLoading}>Stäng av</Button>
                </Popconfirm>
              ) : (
                <Button type="primary" loading={enableMutation.isLoading} onClick={() => enableMutation.mutate()}>
                  Aktivera
                </Button>
              )}
              <Popconfirm
                title="Radera webhook?"
                description="Webhooken och dess leveranslogg raderas. Det går inte att ångra."
                okText="Radera"
                cancelText="Avbryt"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger loading={deleteMutation.isLoading}>
                  Radera
                </Button>
              </Popconfirm>
            </Space>
          </Space>

          {created && (
            <Alert
              type="info"
              showIcon
              message="Webhooken är skapad"
              description="Kopiera API-nyckeln och lägg in den hos mottagaren. Nyckeln syns här så länge webhooken finns."
            />
          )}

          {!subscription.isActive && (
            <Alert
              type={isManuallyDisabled(subscription) ? "info" : "warning"}
              showIcon
              message={isManuallyDisabled(subscription) ? "Webhooken är avstängd manuellt" : "Webhooken stängdes av automatiskt"}
              description={
                <Space direction="vertical" size={4}>
                  {subscription.disabledReason && <span>{subscription.disabledReason}</span>}
                  <span>
                    Avstängd {formatDateTime(subscription.disabledAt)}
                    {subscription.consecutiveFailureCount > 0
                      ? ` · ${subscription.consecutiveFailureCount} misslyckade försök i rad`
                      : ""}
                  </span>
                </Space>
              }
            />
          )}

          {subscription.isActive && subscription.consecutiveFailureCount > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${subscription.consecutiveFailureCount} misslyckade försök i rad`}
              description="Webhooken stängs av automatiskt efter 50 misslyckade försök. Räknaren nollställs vid en lyckad leverans."
            />
          )}
        </Space>
      </Card>

      <Card bordered={false} title="Inställningar">
        <Form form={form} layout="vertical" onFinish={(values) => saveMutation.mutate(values)} style={{ maxWidth: 640 }}>
          <WebhookSubscriptionFields eventTypes={eventTypes} eventTypesLoading={eventTypesQuery.isLoading} />
          <Button type="primary" htmlType="submit" loading={saveMutation.isLoading}>
            Spara
          </Button>
        </Form>
      </Card>

      <Card bordered={false} title="API-nyckel">
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Nyckeln skickas i headern <Typography.Text code>{subscription.apiKeyHeaderName}</Typography.Text> på varje anrop.
            Mottagaren jämför värdet med nyckeln ni ger dem.
          </Typography.Paragraph>
          <div style={keyBoxStyle}>
            <Typography.Text code copyable={{ tooltips: ["Kopiera", "Kopierad"] }}>
              {subscription.apiKey}
            </Typography.Text>
          </div>
          <Popconfirm
            title="Skapa en ny API-nyckel?"
            description="Den gamla nyckeln slutar fungera direkt. Mottagaren måste uppdateras."
            okText="Skapa ny nyckel"
            cancelText="Avbryt"
            onConfirm={() => regenerateMutation.mutate()}
          >
            <Button loading={regenerateMutation.isLoading}>Skapa ny nyckel</Button>
          </Popconfirm>
        </Space>
      </Card>

      <Card bordered={false} title="Test">
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Skickar en <Typography.Text code>webhook.test</Typography.Text> direkt och väntar på svaret. Testet räknas inte
            mot den automatiska avstängningen.
          </Typography.Paragraph>
          <Button loading={testMutation.isLoading} onClick={() => testMutation.mutate()}>
            Skicka test
          </Button>
          {testResult && (
            <Alert
              type={testResult.success ? "success" : "error"}
              showIcon
              message={
                testResult.success
                  ? `Testet lyckades${testResult.statusCode != null ? ` (${testResult.statusCode})` : ""} på ${testResult.durationMs} ms`
                  : `Testet misslyckades${testResult.statusCode != null ? ` (${testResult.statusCode})` : ""} på ${testResult.durationMs} ms`
              }
              description={
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  {testResult.error && <span>{testResult.error}</span>}
                  {testResult.responseBody && (
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 160,
                        overflow: "auto",
                      }}
                    >
                      {testResult.responseBody}
                    </pre>
                  )}
                </Space>
              }
            />
          )}
        </Space>
      </Card>

      <Card bordered={false} title="Leveranser">
        <WebhookDeliveries subscriptionId={subscription.id} />
      </Card>
    </Space>
  );
};
